import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard({ userData, onLogout }) {
  const [activeTab, setActiveTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [loads, setLoads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [managers, setManagers] = useState([]);
  const [pendingDeletions, setPendingDeletions] = useState([]);
  const [trainingModules, setTrainingModules] = useState([]);
  const [newTrainingModule, setNewTrainingModule] = useState({
    title: "",
    category: "Dispatcher Training",
    description: "",
    content: "",
    external_links: [],
    file_attachments: [],
    order: 0,
  });
  const [editingModule, setEditingModule] = useState(null);
  const [newLink, setNewLink] = useState({ name: "", url: "" });
  const [newAttachment, setNewAttachment] = useState({ name: "", url: "" });
  const [newLoad, setNewLoad] = useState({
    origin: "",
    destination: "",
    pickup: "",
    equipment: "Cargo Van",
    weight: "",
    pay: "",
    tags: "",
  });
  const [newAPIKey, setNewAPIKey] = useState({
    name: "",
    permissions: ["read"],
  });
  const [newManager, setNewManager] = useState({
    name: "",
    code: "",
    email: "",
  });
  const [editingLoad, setEditingLoad] = useState(null);

  const isMaster = userData.role === 'master';
  const headers = isMaster 
    ? { "master-code": userData.code }
    : { "manager-code": userData.code };

  useEffect(() => {
    if (activeTab === "analytics") fetchAnalytics();
    if (activeTab === "loads") fetchLoads();
    if (activeTab === "load-board-view") fetchLoads();
    if (activeTab === "training") fetchTrainingModules();
    if (activeTab === "training-management" && isMaster) fetchAllTrainingModules();
    if (activeTab === "bookings") fetchBookings();
    if (activeTab === "carriers") fetchCarriers();
    if (activeTab === "api-keys") fetchAPIKeys();
    if (activeTab === "managers" && isMaster) fetchManagers();
    if (activeTab === "pending-deletions" && isMaster) fetchPendingDeletions();
  }, [activeTab]);

  useEffect(() => {
    // Fetch pending deletions count for master
    if (isMaster) {
      fetchPendingDeletions();
    }
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, { headers });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  const fetchLoads = async () => {
    try {
      const response = await axios.get(`${API}/loads`);
      setLoads(response.data);
    } catch (error) {
      console.error("Error fetching loads:", error);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings`, { headers });
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const fetchCarriers = async () => {
    try {
      const response = await axios.get(`${API}/admin/carriers`, { headers });
      setCarriers(response.data);
    } catch (error) {
      console.error("Error fetching carriers:", error);
    }
  };

  const fetchAPIKeys = async () => {
    try {
      const response = await axios.get(`${API}/admin/api-keys`, { headers });
      setApiKeys(response.data);
    } catch (error) {
      console.error("Error fetching API keys:", error);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/admin/managers`, { headers });
      setManagers(response.data);
    } catch (error) {
      console.error("Error fetching managers:", error);
    }
  };

  const fetchPendingDeletions = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-deletions`, { headers });
      setPendingDeletions(response.data);
    } catch (error) {
      console.error("Error fetching pending deletions:", error);
    }
  };

  const fetchTrainingModules = async () => {
    try {
      const response = await axios.get(`${API}/training-modules`);
      setTrainingModules(response.data);
    } catch (error) {
      console.error("Error fetching training modules:", error);
    }
  };

  const fetchAllTrainingModules = async () => {
    try {
      const response = await axios.get(`${API}/admin/training-modules/all`, { headers });
      setTrainingModules(response.data);
    } catch (error) {
      console.error("Error fetching all training modules:", error);
    }
  };

  const handleCreateLoad = async (e) => {
    e.preventDefault();
    try {
      const loadData = {
        ...newLoad,
        pay: parseFloat(newLoad.pay),
        tags: newLoad.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      await axios.post(`${API}/admin/loads`, loadData, { headers });
      alert("Load created successfully!");
      setNewLoad({
        origin: "",
        destination: "",
        pickup: "",
        equipment: "Cargo Van",
        weight: "",
        pay: "",
        tags: "",
      });
      fetchLoads();
    } catch (error) {
      alert("Error creating load: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateLoad = async (loadId, updates) => {
    try {
      await axios.put(`${API}/admin/loads/${loadId}`, updates, { headers });
      alert("Load updated successfully!");
      setEditingLoad(null);
      fetchLoads();
    } catch (error) {
      alert("Error updating load: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteLoad = async (loadId) => {
    if (!window.confirm("Are you sure you want to delete this load?")) return;
    try {
      const response = await axios.delete(`${API}/admin/loads/${loadId}`, { headers });
      if (response.data.status === "pending_approval") {
        alert(response.data.message);
      } else {
        alert("Load deleted successfully!");
      }
      fetchLoads();
    } catch (error) {
      alert("Error deleting load: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateAPIKey = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/api-keys`, newAPIKey, { headers });
      alert(`API Key created successfully!\n\nKey: ${response.data.key}\n\nSave this key securely - it won't be shown again!`);
      setNewAPIKey({ name: "", permissions: ["read"] });
      fetchAPIKeys();
    } catch (error) {
      alert("Error creating API key: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteAPIKey = async (keyId) => {
    if (!window.confirm("Are you sure you want to delete this API key?")) return;
    try {
      const response = await axios.delete(`${API}/admin/api-keys/${keyId}`, { headers });
      if (response.data.status === "pending_approval") {
        alert(response.data.message);
      } else {
        alert("API key deleted successfully!");
      }
      fetchAPIKeys();
    } catch (error) {
      alert("Error deleting API key: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateManager = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/managers`, newManager, { headers });
      alert(`Manager created successfully!\n\nName: ${newManager.name}\nCode: ${newManager.code}\n\nManager can now login using their 4-digit code.`);
      setNewManager({ name: "", code: "", email: "" });
      fetchManagers();
    } catch (error) {
      alert("Error creating manager: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleManager = async (managerId, isActive) => {
    try {
      await axios.put(`${API}/admin/managers/${managerId}/toggle?is_active=${!isActive}`, {}, { headers });
      alert(`Manager ${!isActive ? "activated" : "deactivated"} successfully!`);
      fetchManagers();
    } catch (error) {
      alert("Error updating manager: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteManager = async (managerId) => {
    if (!window.confirm("Are you sure you want to delete this manager?")) return;
    try {
      await axios.delete(`${API}/admin/managers/${managerId}`, { headers });
      alert("Manager deleted successfully!");
      fetchManagers();
    } catch (error) {
      alert("Error deleting manager: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleApproveDeletion = async (requestId) => {
    if (!window.confirm("Are you sure you want to approve this deletion?")) return;
    try {
      await axios.post(`${API}/admin/pending-deletions/${requestId}/approve`, {}, { headers });
      alert("Deletion approved and executed!");
      fetchPendingDeletions();
      fetchLoads();
      fetchAPIKeys();
    } catch (error) {
      alert("Error approving deletion: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleRejectDeletion = async (requestId) => {
    if (!window.confirm("Are you sure you want to reject this deletion request?")) return;
    try {
      await axios.post(`${API}/admin/pending-deletions/${requestId}/reject`, {}, { headers });
      alert("Deletion request rejected!");
      fetchPendingDeletions();
    } catch (error) {
      alert("Error rejecting deletion: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleVerifyCarrier = async (carrierId, isVerified) => {
    try {
      await axios.put(`${API}/admin/carriers/${carrierId}/verify?is_verified=${isVerified}`, {}, { headers });
      alert(`Carrier ${isVerified ? "verified" : "unverified"} successfully!`);
      fetchCarriers();
    } catch (error) {
      alert("Error updating carrier: " + (error.response?.data?.detail || error.message));
    }
  };

  // Training Module Handlers
  const handleCreateTrainingModule = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/training-modules`, newTrainingModule, { headers });
      alert("Training module created successfully!");
      setNewTrainingModule({
        title: "",
        category: "Dispatcher Training",
        description: "",
        content: "",
        external_links: [],
        file_attachments: [],
        order: 0,
      });
      fetchAllTrainingModules();
    } catch (error) {
      alert("Error creating training module: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateTrainingModule = async (moduleId, updates) => {
    try {
      await axios.put(`${API}/admin/training-modules/${moduleId}`, updates, { headers });
      alert("Training module updated successfully!");
      setEditingModule(null);
      fetchAllTrainingModules();
    } catch (error) {
      alert("Error updating training module: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteTrainingModule = async (moduleId) => {
    if (!window.confirm("Are you sure you want to delete this training module?")) return;
    try {
      await axios.delete(`${API}/admin/training-modules/${moduleId}`, { headers });
      alert("Training module deleted successfully!");
      fetchAllTrainingModules();
    } catch (error) {
      alert("Error deleting training module: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddLink = () => {
    if (newLink.name && newLink.url) {
      setNewTrainingModule({
        ...newTrainingModule,
        external_links: [...newTrainingModule.external_links, { ...newLink }]
      });
      setNewLink({ name: "", url: "" });
    }
  };

  const handleAddAttachment = () => {
    if (newAttachment.name && newAttachment.url) {
      setNewTrainingModule({
        ...newTrainingModule,
        file_attachments: [...newTrainingModule.file_attachments, { ...newAttachment }]
      });
      setNewAttachment({ name: "", url: "" });
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">
            {isMaster ? "Admin Dashboard" : "Manager Dashboard"}
          </h1>
          <p className="admin-subtitle">
            {isMaster ? `Master Code: ${userData.code}` : `Manager: ${userData.name} (${userData.code})`}
          </p>
          {isMaster && pendingDeletions.length > 0 && (
            <div className="pending-badge">
              {pendingDeletions.length} Pending Deletion Request(s)
            </div>
          )}
        </div>
        <button className="btn-ghost" onClick={onLogout}>
          Logout
        </button>
      </div>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === "analytics" ? "active" : ""}`}
          onClick={() => setActiveTab("analytics")}
        >
          Analytics
        </button>
        <button
          className={`admin-tab ${activeTab === "load-board-view" ? "active" : ""}`}
          onClick={() => setActiveTab("load-board-view")}
        >
          View Loads
        </button>
        <button
          className={`admin-tab ${activeTab === "loads" ? "active" : ""}`}
          onClick={() => setActiveTab("loads")}
        >
          Load Management
        </button>
        <button
          className={`admin-tab ${activeTab === "training" ? "active" : ""}`}
          onClick={() => setActiveTab("training")}
        >
          Training & Materials
        </button>
        <button
          className={`admin-tab ${activeTab === "bookings" ? "active" : ""}`}
          onClick={() => setActiveTab("bookings")}
        >
          Bookings
        </button>
        <button
          className={`admin-tab ${activeTab === "carriers" ? "active" : ""}`}
          onClick={() => setActiveTab("carriers")}
        >
          Carriers
        </button>
        <button
          className={`admin-tab ${activeTab === "api-keys" ? "active" : ""}`}
          onClick={() => setActiveTab("api-keys")}
        >
          Broker API Keys
        </button>
        {isMaster && (
          <>
            <button
              className={`admin-tab ${activeTab === "training-management" ? "active" : ""}`}
              onClick={() => setActiveTab("training-management")}
            >
              Manage Training
            </button>
            <button
              className={`admin-tab ${activeTab === "system-overview" ? "active" : ""}`}
              onClick={() => setActiveTab("system-overview")}
            >
              System Overview
            </button>
            <button
              className={`admin-tab ${activeTab === "managers" ? "active" : ""}`}
              onClick={() => setActiveTab("managers")}
            >
              Managers
            </button>
            <button
              className={`admin-tab ${activeTab === "pending-deletions" ? "active" : ""}`}
              onClick={() => setActiveTab("pending-deletions")}
            >
              Pending Deletions
              {pendingDeletions.length > 0 && (
                <span className="tab-badge">{pendingDeletions.length}</span>
              )}
            </button>
          </>
        )}
      </div>

      <div className="admin-content">
        {activeTab === "analytics" && analytics && (
          <div className="analytics-grid">
            <div className="stat-card">
              <h3>Total Loads</h3>
              <p className="stat-number">{analytics.loads.total}</p>
              <div className="stat-breakdown">
                <span>Available: {analytics.loads.available}</span>
                <span>Booked: {analytics.loads.booked}</span>
                <span>In Transit: {analytics.loads.in_transit}</span>
                <span>Delivered: {analytics.loads.delivered}</span>
              </div>
            </div>
            <div className="stat-card">
              <h3>Total Bookings</h3>
              <p className="stat-number">{analytics.bookings.total}</p>
              <div className="stat-breakdown">
                <span>Practice: {analytics.bookings.practice}</span>
                <span>Real: {analytics.bookings.real}</span>
              </div>
            </div>
            <div className="stat-card">
              <h3>Total Carriers</h3>
              <p className="stat-number">{analytics.carriers.total}</p>
              <div className="stat-breakdown">
                <span>Verified: {analytics.carriers.verified}</span>
                <span>Unverified: {analytics.carriers.unverified}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === "load-board-view" && (
          <div>
            <div className="admin-section">
              <h2>Current Loads on Load Board ({loads.length})</h2>
              <p className="section-sub">Visual display of all active loads available to carriers</p>
            </div>
            
            <div className="load-cards-grid">
              {loads.length === 0 ? (
                <div className="info-box">
                  <p>No loads available. Create loads in the "Load Management" tab.</p>
                </div>
              ) : (
                loads.map((load) => (
                  <div key={load.id} className="load-display-card">
                    <div className="load-card-header">
                      <div className="load-route">
                        <span className="load-origin">{load.origin}</span>
                        <span className="load-arrow">→</span>
                        <span className="load-destination">{load.destination}</span>
                      </div>
                      <span className={`load-status-badge status-${load.status}`}>
                        {load.status}
                      </span>
                    </div>
                    
                    <div className="load-card-details">
                      <div className="load-detail-item">
                        <span className="detail-label">Pickup:</span>
                        <span className="detail-value">{load.pickup}</span>
                      </div>
                      <div className="load-detail-item">
                        <span className="detail-label">Equipment:</span>
                        <span className="detail-value">{load.equipment}</span>
                      </div>
                      <div className="load-detail-item">
                        <span className="detail-label">Weight:</span>
                        <span className="detail-value">{load.weight}</span>
                      </div>
                      <div className="load-detail-item">
                        <span className="detail-label">Pay:</span>
                        <span className="detail-value load-pay-amount">${load.pay}</span>
                      </div>
                    </div>
                    
                    {load.tags && load.tags.length > 0 && (
                      <div className="load-card-tags">
                        {load.tags.map((tag, idx) => (
                          <span key={idx} className="load-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    
                    <div className="load-card-footer">
                      <span className="load-id">ID: {load.id.substring(0, 8)}...</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === "training" && (
          <div>
            <div className="admin-section">
              <h2>Training & Study Materials for Dispatching</h2>
              <p className="section-sub">
                Comprehensive resources for dispatchers, brokers, and carriers to improve operations
              </p>
              {isMaster && (
                <button 
                  className="btn-primary" 
                  onClick={() => setActiveTab("training-management")}
                  style={{ marginTop: "1rem" }}
                >
                  Manage Training Materials
                </button>
              )}
            </div>

            {trainingModules.length === 0 ? (
              <div className="info-box">
                <p>No training modules available yet.</p>
                {isMaster && <p>Click "Manage Training Materials" to add content.</p>}
              </div>
            ) : (
              <div className="training-modules-list">
                {["Dispatcher Training", "Carrier Operations", "Broker Best Practices", "Advanced Topics"].map(category => {
                  const categoryModules = trainingModules.filter(m => m.category === category);
                  if (categoryModules.length === 0) return null;
                  
                  return (
                    <div key={category} className="training-category">
                      <h3>{category}</h3>
                      {categoryModules.map((module) => (
                        <div key={module.id} className="training-card">
                          <h4>{module.title}</h4>
                          <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                            {module.description}
                          </p>
                          <div style={{ whiteSpace: "pre-line", marginBottom: "1rem" }}>
                            {module.content}
                          </div>
                          
                          {module.external_links && module.external_links.length > 0 && (
                            <div className="module-links">
                              <strong>External Resources:</strong>
                              {module.external_links.map((link, idx) => (
                                <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="resource-link">
                                  🔗 {link.name}
                                </a>
                              ))}
                            </div>
                          )}
                          
                          {module.file_attachments && module.file_attachments.length > 0 && (
                            <div className="module-attachments">
                              <strong>Downloadable Files:</strong>
                              {module.file_attachments.map((file, idx) => (
                                <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer" className="resource-link">
                                  📄 {file.name}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "training-management" && isMaster && (
          <div>
            <div className="admin-section">
              <h2>Manage Training Materials</h2>
              <p className="section-sub">Create, edit, and organize training content with external links and attachments</p>
              
              <form onSubmit={handleCreateTrainingModule} className="admin-form">
                <h3>Add New Training Module</h3>
                <div className="form-grid">
                  <input
                    placeholder="Title"
                    value={newTrainingModule.title}
                    onChange={(e) => setNewTrainingModule({ ...newTrainingModule, title: e.target.value })}
                    required
                  />
                  <select
                    value={newTrainingModule.category}
                    onChange={(e) => setNewTrainingModule({ ...newTrainingModule, category: e.target.value })}
                  >
                    <option>Dispatcher Training</option>
                    <option>Carrier Operations</option>
                    <option>Broker Best Practices</option>
                    <option>Advanced Topics</option>
                  </select>
                </div>
                
                <textarea
                  placeholder="Description (brief summary)"
                  value={newTrainingModule.description}
                  onChange={(e) => setNewTrainingModule({ ...newTrainingModule, description: e.target.value })}
                  rows="2"
                  style={{ width: "100%", marginBottom: "1rem", padding: "0.75rem", borderRadius: "0.5rem", border: "1px solid var(--border-subtle)", background: "#10101a", color: "var(--text-main)" }}
                  required
                />
                
                <textarea
                  placeholder="Content (main training material)"
                  value={newTrainingModule.content}
                  onChange={(e) => setNewTrainingModule({ ...newTrainingModule, content: e.target.value })}
                  rows="6"
                  style={{ width: "100%", marginBottom: "1rem", padding: "0.75rem", borderRadius: "0.5rem", border: "1px solid var(--border-subtle)", background: "#10101a", color: "var(--text-main)" }}
                  required
                />
                
                <div style={{ marginBottom: "1rem" }}>
                  <h4>Add External Links (YouTube, websites, etc.)</h4>
                  <div className="form-grid">
                    <input
                      placeholder="Link Name (e.g., Training Video)"
                      value={newLink.name}
                      onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                    />
                    <input
                      placeholder="URL (e.g., https://youtube.com/...)"
                      value={newLink.url}
                      onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                    />
                    <button type="button" onClick={handleAddLink} className="btn-ghost">Add Link</button>
                  </div>
                  {newTrainingModule.external_links.length > 0 && (
                    <div style={{ marginTop: "0.5rem" }}>
                      {newTrainingModule.external_links.map((link, idx) => (
                        <div key={idx} className="badge" style={{ marginRight: "0.5rem" }}>
                          🔗 {link.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div style={{ marginBottom: "1rem" }}>
                  <h4>Add File Attachments (Google Drive, Dropbox, etc.)</h4>
                  <div className="form-grid">
                    <input
                      placeholder="File Name (e.g., Quick Reference Guide.pdf)"
                      value={newAttachment.name}
                      onChange={(e) => setNewAttachment({ ...newAttachment, name: e.target.value })}
                    />
                    <input
                      placeholder="URL (e.g., https://drive.google.com/...)"
                      value={newAttachment.url}
                      onChange={(e) => setNewAttachment({ ...newAttachment, url: e.target.value })}
                    />
                    <button type="button" onClick={handleAddAttachment} className="btn-ghost">Add File</button>
                  </div>
                  {newTrainingModule.file_attachments.length > 0 && (
                    <div style={{ marginTop: "0.5rem" }}>
                      {newTrainingModule.file_attachments.map((file, idx) => (
                        <div key={idx} className="badge" style={{ marginRight: "0.5rem" }}>
                          📄 {file.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <button type="submit" className="btn-primary">Create Training Module</button>
              </form>
            </div>

            <div className="admin-section">
              <h2>All Training Modules ({trainingModules.length})</h2>
              <div className="admin-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Links</th>
                      <th>Files</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trainingModules.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: "center", padding: "1rem" }}>
                          No training modules yet. Create one above!
                        </td>
                      </tr>
                    ) : (
                      trainingModules.map((module) => (
                        <tr key={module.id}>
                          <td><strong>{module.title}</strong></td>
                          <td>{module.category}</td>
                          <td>{module.external_links?.length || 0}</td>
                          <td>{module.file_attachments?.length || 0}</td>
                          <td>
                            <span className={`badge ${module.is_active ? "badge-verified" : "badge-unverified"}`}>
                              {module.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn-danger-small"
                              onClick={() => handleDeleteTrainingModule(module.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "system-overview" && isMaster && (
          <div>
            <div className="admin-section">
              <h2>Training & Study Materials for Dispatching</h2>
              <p className="section-sub">
                Comprehensive resources for dispatchers, brokers, and carriers to improve operations
              </p>
            </div>

            <div className="training-grid">
              <div className="training-category">
                <h3>📚 Dispatcher Training</h3>
                <div className="training-card">
                  <h4>1. Load Board Basics</h4>
                  <ul>
                    <li>Understanding load requirements</li>
                    <li>Equipment types and specifications</li>
                    <li>Reading weight and dimension restrictions</li>
                    <li>Geographic routing optimization</li>
                  </ul>
                </div>
                
                <div className="training-card">
                  <h4>2. Carrier Communication</h4>
                  <ul>
                    <li>Professional phone etiquette</li>
                    <li>Rate negotiation strategies</li>
                    <li>Handling carrier objections</li>
                    <li>Building long-term relationships</li>
                  </ul>
                </div>
                
                <div className="training-card">
                  <h4>3. Load Posting & Marketing</h4>
                  <ul>
                    <li>Writing effective load descriptions</li>
                    <li>Pricing strategies for competitive rates</li>
                    <li>Highlighting special requirements</li>
                    <li>Using tags effectively (Final Mile, Hot Shot, etc.)</li>
                  </ul>
                </div>
              </div>

              <div className="training-category">
                <h3>🚛 Carrier Operations</h3>
                <div className="training-card">
                  <h4>4. MC/DOT Compliance</h4>
                  <ul>
                    <li>FMCSA registration requirements</li>
                    <li>Insurance documentation</li>
                    <li>Hours of Service (HOS) regulations</li>
                    <li>Vehicle inspection procedures</li>
                  </ul>
                </div>
                
                <div className="training-card">
                  <h4>5. Load Booking Process</h4>
                  <ul>
                    <li>Subscription vs. preview mode</li>
                    <li>How to book loads effectively</li>
                    <li>Payment terms and expectations</li>
                    <li>Tracking and proof of delivery</li>
                  </ul>
                </div>
              </div>

              <div className="training-category">
                <h3>💼 Broker Best Practices</h3>
                <div className="training-card">
                  <h4>6. API Integration Guide</h4>
                  <ul>
                    <li>Generating and managing API keys</li>
                    <li>Automatic load posting workflow</li>
                    <li>Receiving booking notifications</li>
                    <li>Status update automation</li>
                  </ul>
                </div>
                
                <div className="training-card">
                  <h4>7. Customer Service Excellence</h4>
                  <ul>
                    <li>Handling shipper complaints</li>
                    <li>Carrier performance management</li>
                    <li>Emergency load coverage procedures</li>
                    <li>Building reputation and trust</li>
                  </ul>
                </div>
              </div>

              <div className="training-category">
                <h3>📊 Advanced Topics</h3>
                <div className="training-card">
                  <h4>8. Analytics & Optimization</h4>
                  <ul>
                    <li>Reading load board analytics</li>
                    <li>Identifying profitable lanes</li>
                    <li>Seasonal demand patterns</li>
                    <li>Carrier performance metrics</li>
                  </ul>
                </div>
                
                <div className="training-card">
                  <h4>9. Problem Resolution</h4>
                  <ul>
                    <li>Handling late pickups/deliveries</li>
                    <li>Managing damaged freight claims</li>
                    <li>Carrier no-show procedures</li>
                    <li>Payment dispute resolution</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="admin-section" style={{ marginTop: "2rem" }}>
              <h3>📖 Downloadable Resources</h3>
              <div className="resource-links">
                <div className="resource-item">
                  <span className="resource-icon">📄</span>
                  <div>
                    <h4>Dispatcher Quick Reference Guide</h4>
                    <p>Essential checklist for daily dispatch operations</p>
                  </div>
                </div>
                <div className="resource-item">
                  <span className="resource-icon">📋</span>
                  <div>
                    <h4>Carrier Onboarding Checklist</h4>
                    <p>Step-by-step guide for vetting new carriers</p>
                  </div>
                </div>
                <div className="resource-item">
                  <span className="resource-icon">💰</span>
                  <div>
                    <h4>Rate Negotiation Scripts</h4>
                    <p>Proven conversation templates for better rates</p>
                  </div>
                </div>
                <div className="resource-item">
                  <span className="resource-icon">⚠️</span>
                  <div>
                    <h4>Emergency Response Procedures</h4>
                    <p>Protocols for handling critical situations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "system-overview" && isMaster && (
          <div>
            <div className="admin-section">
              <h2>System Overview - Master Admin Only</h2>
              <p className="section-sub">Complete view of all system services, activity, and performance</p>
            </div>

            <div className="system-grid">
              <div className="system-card">
                <h3>🖥️ Services Status</h3>
                <div className="service-status-list">
                  <div className="service-item">
                    <span className="service-indicator running"></span>
                    <span className="service-name">Backend API</span>
                    <span className="service-detail">Running on port 8001</span>
                  </div>
                  <div className="service-item">
                    <span className="service-indicator running"></span>
                    <span className="service-name">Frontend</span>
                    <span className="service-detail">Deployed at {window.location.origin}</span>
                  </div>
                  <div className="service-item">
                    <span className="service-indicator running"></span>
                    <span className="service-name">MongoDB</span>
                    <span className="service-detail">Connected</span>
                  </div>
                  <div className="service-item">
                    <span className="service-indicator running"></span>
                    <span className="service-name">Stripe Payments</span>
                    <span className="service-detail">Active (Test Mode)</span>
                  </div>
                </div>
              </div>

              {analytics && (
                <>
                  <div className="system-card">
                    <h3>📊 Database Statistics</h3>
                    <div className="db-stats">
                      <div className="db-stat-item">
                        <span className="db-stat-label">Total Loads:</span>
                        <span className="db-stat-value">{analytics.loads.total}</span>
                      </div>
                      <div className="db-stat-item">
                        <span className="db-stat-label">Total Bookings:</span>
                        <span className="db-stat-value">{analytics.bookings.total}</span>
                      </div>
                      <div className="db-stat-item">
                        <span className="db-stat-label">Total Carriers:</span>
                        <span className="db-stat-value">{analytics.carriers.total}</span>
                      </div>
                      <div className="db-stat-item">
                        <span className="db-stat-label">Active Managers:</span>
                        <span className="db-stat-value">{managers.length}</span>
                      </div>
                      <div className="db-stat-item">
                        <span className="db-stat-label">API Keys:</span>
                        <span className="db-stat-value">{apiKeys.length}</span>
                      </div>
                      <div className="db-stat-item">
                        <span className="db-stat-label">Pending Deletions:</span>
                        <span className="db-stat-value">{pendingDeletions.length}</span>
                      </div>
                    </div>
                  </div>

                  <div className="system-card">
                    <h3>💰 Revenue Overview</h3>
                    <div className="revenue-stats">
                      <div className="revenue-item">
                        <span className="revenue-label">Subscribed Carriers:</span>
                        <span className="revenue-value">
                          {/* Calculate from subscriptions */}
                          {analytics.carriers.total} carriers
                        </span>
                      </div>
                      <div className="revenue-item">
                        <span className="revenue-label">Monthly Revenue Potential:</span>
                        <span className="revenue-value highlight">
                          ${(analytics.carriers.total * 49.99).toFixed(2)}
                        </span>
                      </div>
                      <div className="revenue-item">
                        <span className="revenue-label">Annual Projection:</span>
                        <span className="revenue-value">
                          ${(analytics.carriers.total * 49.99 * 12).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="system-card">
                <h3>🔐 Security & Access</h3>
                <div className="security-info">
                  <div className="security-item">
                    <strong>Master Code:</strong> 3357 (You)
                  </div>
                  <div className="security-item">
                    <strong>Active Managers:</strong> {managers.length}
                  </div>
                  <div className="security-item">
                    <strong>API Keys Generated:</strong> {apiKeys.length}
                  </div>
                  <div className="security-item">
                    <strong>Pending Approvals:</strong> {pendingDeletions.length}
                  </div>
                </div>
              </div>

              <div className="system-card">
                <h3>🌐 Integration Status</h3>
                <div className="integration-list">
                  <div className="integration-item">
                    <span className="integration-name">Stripe Payment Gateway</span>
                    <span className="integration-status active">Connected</span>
                  </div>
                  <div className="integration-item">
                    <span className="integration-name">Broker API</span>
                    <span className="integration-status active">Available</span>
                  </div>
                  <div className="integration-item">
                    <span className="integration-name">Webhook Endpoint</span>
                    <span className="integration-status active">Configured</span>
                  </div>
                  <div className="integration-item">
                    <span className="integration-name">MongoDB Database</span>
                    <span className="integration-status active">Online</span>
                  </div>
                </div>
              </div>

              <div className="system-card">
                <h3>⚙️ System Configuration</h3>
                <div className="config-info">
                  <div className="config-item">
                    <span className="config-label">Environment:</span>
                    <span className="config-value">Production</span>
                  </div>
                  <div className="config-item">
                    <span className="config-label">Stripe Mode:</span>
                    <span className="config-value">Test Mode</span>
                  </div>
                  <div className="config-item">
                    <span className="config-label">Frontend URL:</span>
                    <span className="config-value">{window.location.origin}</span>
                  </div>
                  <div className="config-item">
                    <span className="config-label">Backend URL:</span>
                    <span className="config-value">{BACKEND_URL}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "loads" && (
          <div>
            <div className="admin-section">
              <h2>Create New Load</h2>
              <form onSubmit={handleCreateLoad} className="admin-form">
                <div className="form-grid">
                  <input
                    placeholder="Origin (e.g., Atlanta, GA)"
                    value={newLoad.origin}
                    onChange={(e) => setNewLoad({ ...newLoad, origin: e.target.value })}
                    required
                  />
                  <input
                    placeholder="Destination (e.g., Charlotte, NC)"
                    value={newLoad.destination}
                    onChange={(e) => setNewLoad({ ...newLoad, destination: e.target.value })}
                    required
                  />
                  <input
                    placeholder="Pickup (e.g., Today • 16:00-18:00)"
                    value={newLoad.pickup}
                    onChange={(e) => setNewLoad({ ...newLoad, pickup: e.target.value })}
                    required
                  />
                  <select
                    value={newLoad.equipment}
                    onChange={(e) => setNewLoad({ ...newLoad, equipment: e.target.value })}
                  >
                    <option>Cargo Van</option>
                    <option>Sprinter Van</option>
                    <option>Box Truck 16'</option>
                    <option>Box Truck 26'</option>
                  </select>
                  <input
                    placeholder="Weight (e.g., 1,200 lb)"
                    value={newLoad.weight}
                    onChange={(e) => setNewLoad({ ...newLoad, weight: e.target.value })}
                    required
                  />
                  <input
                    type="number"
                    placeholder="Pay ($)"
                    value={newLoad.pay}
                    onChange={(e) => setNewLoad({ ...newLoad, pay: e.target.value })}
                    required
                  />
                  <input
                    placeholder="Tags (comma-separated)"
                    value={newLoad.tags}
                    onChange={(e) => setNewLoad({ ...newLoad, tags: e.target.value })}
                  />
                </div>
                <button type="submit" className="btn-primary">
                  Create Load
                </button>
              </form>
            </div>

            <div className="admin-section">
              <h2>All Loads ({loads.length})</h2>
              <div className="admin-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Origin → Destination</th>
                      <th>Pickup</th>
                      <th>Equipment</th>
                      <th>Pay</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loads.map((load) => (
                      <tr key={load.id}>
                        <td>
                          <strong>{load.origin}</strong> → <strong>{load.destination}</strong>
                        </td>
                        <td>{load.pickup}</td>
                        <td>{load.equipment}</td>
                        <td className="lb-pay">${load.pay}</td>
                        <td>
                          <select
                            value={load.status}
                            onChange={(e) => handleUpdateLoad(load.id, { status: e.target.value })}
                            className="status-select"
                          >
                            <option value="available">Available</option>
                            <option value="booked">Booked</option>
                            <option value="in-transit">In Transit</option>
                            <option value="delivered">Delivered</option>
                          </select>
                        </td>
                        <td>
                          <button
                            className="btn-danger-small"
                            onClick={() => handleDeleteLoad(load.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "bookings" && (
          <div className="admin-section">
            <h2>All Bookings ({bookings.length})</h2>
            <div className="admin-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>Load ID</th>
                    <th>Carrier ID</th>
                    <th>Type</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.id.substring(0, 8)}...</td>
                      <td>{booking.load_id.substring(0, 8)}...</td>
                      <td>{booking.carrier_id}</td>
                      <td>
                        <span className={`badge ${booking.is_practice ? "badge-practice" : "badge-real"}`}>
                          {booking.is_practice ? "Practice" : "Real"}
                        </span>
                      </td>
                      <td>{new Date(booking.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "carriers" && (
          <div className="admin-section">
            <h2>All Carriers ({carriers.length})</h2>
            <div className="admin-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Carrier Name</th>
                    <th>MC Number</th>
                    <th>DOT Number</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {carriers.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center", padding: "1rem" }}>
                        No carriers registered yet
                      </td>
                    </tr>
                  ) : (
                    carriers.map((carrier) => (
                      <tr key={carrier.id}>
                        <td>{carrier.carrier_name}</td>
                        <td>{carrier.mc_number || "N/A"}</td>
                        <td>{carrier.dot_number || "N/A"}</td>
                        <td>{carrier.contact_email || "N/A"}</td>
                        <td>
                          <span className={`badge ${carrier.is_verified ? "badge-verified" : "badge-unverified"}`}>
                            {carrier.is_verified ? "Verified" : "Unverified"}
                          </span>
                        </td>
                        <td>
                          <button
                            className={carrier.is_verified ? "btn-danger-small" : "btn-success-small"}
                            onClick={() => handleVerifyCarrier(carrier.id, !carrier.is_verified)}
                          >
                            {carrier.is_verified ? "Unverify" : "Verify"}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "api-keys" && (
          <div>
            <div className="admin-section">
              <h2>Create Broker API Key</h2>
              <form onSubmit={handleCreateAPIKey} className="admin-form">
                <div className="form-grid">
                  <input
                    placeholder="API Key Name (e.g., Broker Software Integration)"
                    value={newAPIKey.name}
                    onChange={(e) => setNewAPIKey({ ...newAPIKey, name: e.target.value })}
                    required
                  />
                  <div>
                    <label style={{ marginRight: "1rem" }}>
                      <input
                        type="checkbox"
                        checked={newAPIKey.permissions.includes("read")}
                        onChange={(e) => {
                          const perms = e.target.checked
                            ? [...newAPIKey.permissions, "read"]
                            : newAPIKey.permissions.filter((p) => p !== "read");
                          setNewAPIKey({ ...newAPIKey, permissions: perms });
                        }}
                      />
                      Read
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={newAPIKey.permissions.includes("write")}
                        onChange={(e) => {
                          const perms = e.target.checked
                            ? [...newAPIKey.permissions, "write"]
                            : newAPIKey.permissions.filter((p) => p !== "write");
                          setNewAPIKey({ ...newAPIKey, permissions: perms });
                        }}
                      />
                      Write
                    </label>
                  </div>
                </div>
                <button type="submit" className="btn-primary">
                  Generate API Key
                </button>
              </form>
              <div className="api-docs">
                <h3>Broker API Documentation</h3>
                <p>Use these endpoints with your API key in the <code>X-API-Key</code> header:</p>
                <ul>
                  <li><code>POST /api/broker/loads</code> - Create a new load (requires write permission)</li>
                  <li><code>PUT /api/broker/loads/:id</code> - Update load status (requires write permission)</li>
                  <li><code>GET /api/broker/loads</code> - Get all loads (requires read permission)</li>
                  <li><code>GET /api/broker/bookings</code> - Get all bookings (requires read permission)</li>
                </ul>
              </div>
            </div>

            <div className="admin-section">
              <h2>Active API Keys ({apiKeys.length})</h2>
              <div className="admin-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Key</th>
                      <th>Permissions</th>
                      <th>Last Used</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiKeys.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center", padding: "1rem" }}>
                          No API keys created yet
                        </td>
                      </tr>
                    ) : (
                      apiKeys.map((key) => (
                        <tr key={key.id}>
                          <td>{key.name}</td>
                          <td>
                            <code>{key.key.substring(0, 20)}...</code>
                          </td>
                          <td>{key.permissions.join(", ")}</td>
                          <td>{key.last_used ? new Date(key.last_used).toLocaleString() : "Never"}</td>
                          <td>
                            <button
                              className="btn-danger-small"
                              onClick={() => handleDeleteAPIKey(key.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "managers" && isMaster && (
          <div>
            <div className="admin-section">
              <h2>Create New Manager</h2>
              <form onSubmit={handleCreateManager} className="admin-form">
                <div className="form-grid">
                  <input
                    placeholder="Manager Name"
                    value={newManager.name}
                    onChange={(e) => setNewManager({ ...newManager, name: e.target.value })}
                    required
                  />
                  <input
                    placeholder="4-Digit Code"
                    value={newManager.code}
                    onChange={(e) => setNewManager({ ...newManager, code: e.target.value })}
                    maxLength="4"
                    pattern="[0-9]{4}"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email (optional)"
                    value={newManager.email}
                    onChange={(e) => setNewManager({ ...newManager, email: e.target.value })}
                  />
                </div>
                <button type="submit" className="btn-primary">
                  Create Manager
                </button>
              </form>
              <div className="info-box">
                <strong>Manager Permissions:</strong>
                <ul>
                  <li>Full access to load board, bookings, carriers, and API keys</li>
                  <li>Can create and update all resources</li>
                  <li>Deletion requests require Master Admin (3357) approval</li>
                  <li>Manager will be notified if deletion is approved/rejected</li>
                </ul>
              </div>
            </div>

            <div className="admin-section">
              <h2>All Managers ({managers.length})</h2>
              <div className="admin-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Code</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managers.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: "center", padding: "1rem" }}>
                          No managers created yet
                        </td>
                      </tr>
                    ) : (
                      managers.map((manager) => (
                        <tr key={manager.id}>
                          <td>{manager.name}</td>
                          <td><code>{manager.code}</code></td>
                          <td>{manager.email || "N/A"}</td>
                          <td>
                            <span className={`badge ${manager.is_active ? "badge-verified" : "badge-unverified"}`}>
                              {manager.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>{new Date(manager.created_at).toLocaleDateString()}</td>
                          <td>
                            <button
                              className={manager.is_active ? "btn-danger-small" : "btn-success-small"}
                              onClick={() => handleToggleManager(manager.id, manager.is_active)}
                              style={{ marginRight: "0.5rem" }}
                            >
                              {manager.is_active ? "Deactivate" : "Activate"}
                            </button>
                            <button
                              className="btn-danger-small"
                              onClick={() => handleDeleteManager(manager.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "pending-deletions" && isMaster && (
          <div className="admin-section">
            <h2>Pending Deletion Requests ({pendingDeletions.length})</h2>
            {pendingDeletions.length === 0 ? (
              <div className="info-box">
                <p>No pending deletion requests at this time.</p>
              </div>
            ) : (
              <div className="deletion-requests">
                {pendingDeletions.map((request) => (
                  <div key={request.id} className="deletion-card">
                    <div className="deletion-header">
                      <div>
                        <h3>{request.resource_type.toUpperCase()} Deletion Request</h3>
                        <p className="deletion-meta">
                          Requested by: <strong>{request.manager_name}</strong> (Code: {request.manager_code})
                        </p>
                        <p className="deletion-meta">
                          Requested: {new Date(request.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="deletion-actions">
                        <button
                          className="btn-success-small"
                          onClick={() => handleApproveDeletion(request.id)}
                        >
                          Approve
                        </button>
                        <button
                          className="btn-danger-small"
                          onClick={() => handleRejectDeletion(request.id)}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                    <div className="deletion-details">
                      <h4>Resource Details:</h4>
                      {request.resource_type === "load" && (
                        <ul>
                          <li><strong>Route:</strong> {request.resource_data.origin} → {request.resource_data.destination}</li>
                          <li><strong>Equipment:</strong> {request.resource_data.equipment}</li>
                          <li><strong>Pay:</strong> ${request.resource_data.pay}</li>
                        </ul>
                      )}
                      {request.resource_type === "api_key" && (
                        <ul>
                          <li><strong>Name:</strong> {request.resource_data.name}</li>
                          <li><strong>Permissions:</strong> {request.resource_data.permissions?.join(", ")}</li>
                          <li><strong>Key:</strong> {request.resource_data.key_preview}</li>
                        </ul>
                      )}
                      {request.resource_type === "carrier" && (
                        <ul>
                          <li><strong>Details:</strong> {JSON.stringify(request.resource_data)}</li>
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
